import type { KeyboardEvent, ReactNode } from "react";

/**
 * ONE RANKING ROW, for both ranked populations.
 *
 * The sibling of {@link RankingBandGroups}: that one owns the shell around a band, this one owns
 * the row inside it. Together they are the reason a company ranking and a candidate ranking read
 * as the same screen with different subjects, rather than as two screens.
 *
 * What lives here is everything a reader perceives as "the layout": the row rhythm and its divider,
 * the click target over the whole header, the hover and focus treatment, the fixed-width name
 * column that makes the badge columns line up ACROSS rows, the chevron and its rotation, and the
 * open/close animation.
 *
 * What deliberately does NOT live here: what sits in the bubble (an Avatar, a CompanyLogo), which
 * badges follow the name, and what the drill-down shows. Those are the subject, and the subject is
 * the caller's business.
 *
 * THE FIXED NAME COLUMN IS THE POINT, not a detail. `w-[15rem] shrink-0` is what puts every row's
 * badges at the same x-position; with a flexing name column they walk with the length of each name
 * and the list loses its grid. It was that difference, not the badges, that made the two rankings
 * look unrelated.
 */
export function RankingRow({
  open,
  onToggle,
  bubble,
  name,
  meta,
  badges,
  toggleLabel,
  id,
  toggleId,
  testId,
  anchorAttribute,
  children,
}: {
  open: boolean;
  onToggle: () => void;
  /**
   * The accessible name of the row's click target.
   *
   * REQUIRED, because without it the name is the row's whole text content: a screen reader would
   * announce "Nordwerk GmbH Muss 3/4 Kann 2,4/3 ..." as the button's name, and any test asking for
   * a button called /Muss/ would match both the row and the Muss pill. The wording stays with the
   * caller, so each population names its own evidence.
   */
  toggleLabel: string;
  /** Usually a link carrying an Avatar or a CompanyLogo. Stops its own propagation. */
  bubble: ReactNode;
  name: ReactNode;
  /** Optional second line under the name. The candidate side has none, the company side has one. */
  meta?: ReactNode;
  badges: ReactNode;
  id?: string;
  toggleId?: string;
  testId?: string;
  /** e.g. `{ "data-person-anchor": personId }` for the scroll anchor. */
  anchorAttribute?: Record<string, string>;
  /** The drill-down. Rendered inside the animated container and inert while closed. */
  children?: ReactNode;
}) {
  function onKeyDown(event: KeyboardEvent<HTMLDivElement>) {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      onToggle();
    }
  }

  return (
    <li
      id={id}
      className="group/ranking-row border-t border-ink/5 py-2.5 first:border-t-0"
      data-testid={testId}
      {...anchorAttribute}
    >
      {/* The negative margins let the hover surface reach past the row's own padding, so the
          highlight covers the full row instead of a box inset within it. */}
      <div
        id={toggleId}
        role="button"
        tabIndex={0}
        aria-expanded={open}
        aria-label={toggleLabel}
        onClick={onToggle}
        onKeyDown={onKeyDown}
        className="-mx-2 -my-2.5 flex flex-wrap cursor-pointer items-center gap-3 rounded-md px-2 py-2.5 outline-none transition-colors hover:bg-accent/5 focus-visible:ring-2 focus-visible:ring-ink-accent-ui"
      >
        <div className="shrink-0">{bubble}</div>
        <div className="flex min-w-0 flex-1 lg:w-[15rem] lg:flex-none items-center gap-1.5 py-1.5">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-1.5">
              <span className="smycles-field-value min-w-0 truncate text-ink transition group-hover/ranking-row:text-ink-accent">
                {name}
              </span>
              <span
                aria-hidden="true"
                className={`shrink-0 text-smycles-label text-ink-muted transition-transform duration-[var(--duration-base)] ${
                  open ? "rotate-90" : ""
                }`}
              >
                ›
              </span>
            </div>
            {meta != null ? <div className="truncate text-smycles-label text-ink-muted">{meta}</div> : null}
          </div>
        </div>
        <div className="flex w-full min-w-0 flex-wrap items-center gap-1.5 lg:w-auto lg:flex-1">{badges}</div>
      </div>
      {/* grid-rows 0fr -> 1fr animates a height nobody has measured. `inert` keeps the collapsed
          content out of the tab order and off the accessibility tree, which a plain `hidden` would
          do too — but this one can animate. */}
      <div
        inert={!open}
        className={`grid transition-[grid-template-rows] duration-[var(--duration-base)] ${
          open ? "grid-rows-[1fr]" : "grid-rows-[0fr]"
        }`}
      >
        <div className="overflow-hidden">{children}</div>
      </div>
    </li>
  );
}
